import React, { useState } from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { ThumbUp as ReceiveIcon, ThumbDown as RejectIcon } from '@material-ui/icons';
import { Invoice } from '../../models/InvoiceModel';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import { ConfirmationState } from '../../types/ConfirmationDialog';

interface InvoiceActionButtonsProps {
  invoice: Invoice;
  onReceive?: (invoice: Invoice) => void;
  onReject?: (invoice: Invoice) => void;
}

/**
 * アクションボタンのスタイル定義
 */
const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    // 画面幅が小さい場合、ボタンを縦並びにする
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
      '& > *': {
        width: '100%',
      },
    },
  },
  receiveButton: {
    background: 'linear-gradient(45deg, #66BB6A 30%, #81C784 90%)',
    border: 'none',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 3px 5px 2px rgba(102, 187, 106, .3)',
    color: 'white',
    fontWeight: 'bold',
    padding: theme.spacing(1, 1),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
      boxShadow: '0 6px 10px 4px rgba(102, 187, 106, .3)',
      transform: 'translateY(-2px) scale(1.05)',
    },
    '&:active': {
      transform: 'translateY(0) scale(0.98)',
    },
  },
  rejectButton: {
    background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E8E 90%)',
    border: 'none',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: 'white',
    fontWeight: 'bold',
    padding: theme.spacing(1, 1),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: 'linear-gradient(45deg, #FF5252 30%, #FF7777 90%)',
      boxShadow: '0 6px 10px 4px rgba(255, 105, 135, .3)',
      transform: 'translateY(-2px) scale(1.05)',
    },
    '&:active': {
      transform: 'translateY(0) scale(0.98)',
    },
  },
}));

/**
 * 請求書アクションボタンコンポーネント
 *
 * 受領権限を持つユーザーが確認状態のInvoiceに対して実行できるアクションボタン群
 * 各アクションには確認ダイアログが表示される
 */
const InvoiceActionButtons: React.FC<InvoiceActionButtonsProps> = ({
  invoice,
  onReceive,
  onReject,
}) => {
  const classes = useStyles();

  // 確認ダイアログの状態管理
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: undefined,
  });

  const [isLoading, setIsLoading] = useState(false);

  // 受領権限チェック
  const hasReceivePermission = invoice.to_assignment?.role === 1;
  // 確認状態チェック
  const isInReviewState = invoice.state === 0;

  // 受領権限があり、確認状態の場合のみ表示
  if (!hasReceivePermission || !isInReviewState) {
    return null;
  }

  /**
   * 受領ボタン押下時の処理
   * 確認ダイアログを表示する
   */
  const handleReceiveClick = () => {
    setConfirmationState({
      isOpen: true,
      title: '請求書の受領確認',
      severity: 'success',
      message: `【 ${invoice.work}】を受領しますか？\n\nこの操作は取り消すことができません。`,
      onConfirm: () => {
        onReceive?.(invoice);
        closeConfirmationDialog();
      },
    });
  };

  /**
   * 却下ボタン押下時の処理
   * 確認ダイアログを表示する
   */
  const handleRejectClick = () => {
    setConfirmationState({
      isOpen: true,
      title: '請求書の却下確認',
      severity: 'error',
      message: `【 ${invoice.work}】を却下しますか？\n\n却下後は再提出が必要になります。`,
      onConfirm: () => {
        onReject?.(invoice);
        closeConfirmationDialog();
      },
    });
  };

  /**
   * 確認ダイアログを閉じる
   */
  const closeConfirmationDialog = () => {
    setConfirmationState({
      isOpen: false,
      title: '',
      severity: confirmationState.severity,
      message: '',
      onConfirm: undefined,
    });
  };

  // if (isLoading) {
  //   return (
  //     <div className={classes.buttonContainer}>
  //       <CircularProgress />
  //     </div>
  //   );
  // }
  return (
    <>
      <div className={classes.buttonContainer}>
        {/* 受領ボタン */}
        <Button
          variant="contained"
          size="small"
          className={classes.receiveButton}
          startIcon={<ReceiveIcon />}
          onClick={handleReceiveClick}
        >
          受領
        </Button>

        {/* 却下ボタン */}
        <Button
          variant="contained"
          size="small"
          className={classes.rejectButton}
          startIcon={<RejectIcon />}
          onClick={handleRejectClick}
        >
          却下
        </Button>
      </div>

      {/* 確認ダイアログ */}
      <ConfirmationDialog
        open={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        severity={confirmationState.severity}
        confirmText="実行"
        cancelText="キャンセル"
        confirmColor="primary"
        onConfirm={confirmationState.onConfirm || (() => {})}
        onCancel={closeConfirmationDialog}
      />
    </>
  );
};

export default InvoiceActionButtons;
